/**
 * A sound the operator uploaded, held where the phone can reach it.
 *
 * There are two of these - the ring for a call coming in and the ring for one
 * going out - and both answer the same three questions in the same way, so the
 * holder is a factory rather than two copies of the same six lines.
 *
 * WHY A MODULE-LEVEL HOLDER AND NOT STATE. The address is pushed in from the
 * voice token response rather than fetched where it is played. A ring is needed
 * at the moment a call starts, which is far too late to ask a settings endpoint
 * anything; the token is already fetched before the device registers, so the
 * answer is in the building long before anything can ring. Holding it here
 * rather than threading it through the call provider is the same call
 * `audio/choice.ts` makes about the speaker: the card that mounts a ring has
 * nothing to say about which sound the office phone makes.
 *
 * ANYTHING NOT SAME-ORIGIN IS REFUSED rather than stored. The value arrives
 * from our own API, so a rejection means something upstream is wrong, and the
 * useful failure is a phone that still rings.
 */

export interface OwnSound {
  /** Record the operator's own file, or clear it back to the default. */
  set(url: string | null | undefined): void;
  /** The address to play. Empty when there is no default to fall back to. */
  url(): string;
  /** True when the operator has chosen their own sound. */
  hasCustom(): boolean;
}

/**
 * `fallback` is what plays when nothing was uploaded.
 *
 * Empty is a legal fallback and means "there is no sound of ours for this" -
 * the outgoing ring, where the default belongs to the Voice SDK and not to us.
 * A caller that passes empty has to check `hasCustom()` before playing, which
 * is exactly the decision it should be making anyway.
 */
export function createOwnSound(fallback: string): OwnSound {
  let custom: string | null = null;

  return {
    set(url) {
      if (typeof url !== "string") {
        custom = null;
        return;
      }
      const trimmed = url.trim();
      custom = trimmed.startsWith("/") && !trimmed.startsWith("//") ? trimmed : null;
    },
    url: () => custom ?? fallback,
    hasCustom: () => custom !== null,
  };
}
